// vim: set ts=2 sts=2 sw=2 et:

import React, { useState } from "react";
import { connect } from "react-redux";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import type { Dispatch } from "redux";

import { overwriteStore } from "../../actions/globalActions";
import type { GlobalState } from "../../types/stateTypes";
import { getDeviceId } from "./deviceIdentity";
import { saveOpenlifterFile } from "./saveOpenlifterFile";
import { fetchDeviceSnapshot, listOtherDevices } from "./syncClient";
import type { SyncDeviceSummary } from "./syncClient";
import { backupFromWebError, describeSummary, stateFromWebBackup, summarizeMeet, syncUrlFrom } from "./webBackup";

import styles from "./WebBackup.module.scss";

type StateProps = { global: GlobalState };
type DispatchProps = { load: (state: GlobalState) => void };
type Props = StateProps & DispatchProps;

const dateLabel = (iso: string): string =>
  new Date(iso).toLocaleString("es-AR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const copyName = (copy: SyncDeviceSummary): string =>
  `${copy.label || "PC sin nombre"}${copy.deviceId === getDeviceId() ? " (esta PC)" : ""}`;

/**
 * "Recuperar de la web" (Inicio): lista las copias que subieron las OTRAS PCs de este torneo y carga
 * la elegida. Antes descarga un .openlifter con lo que hay en esta PC, y conserva la sesión con la web,
 * la tarima en vivo y el lugar donde está operando.
 */
const WebRestoreButtonInner = ({ global, load }: Props) => {
  const [busy, setBusy] = useState(false);
  const [copies, setCopies] = useState<SyncDeviceSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const session = global.wrpfSession;
  const syncUrl = syncUrlFrom(session);
  const token = session.callbackToken;
  const linked = Boolean(session.tournamentId && token && syncUrl);

  const handleOpen = async () => {
    if (!syncUrl || !token) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // "" no excluye ninguna PC: también aparece la copia de esta, por si esta PC perdió su torneo.
      setCopies(await listOtherDevices(syncUrl, token, ""));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleLoad = async (copy: SyncDeviceSummary) => {
    if (!syncUrl || !token) return;
    setBusy(true);
    setError(null);
    try {
      const payload = await fetchDeviceSnapshot(syncUrl, token, copy.deviceId);
      const invalid = backupFromWebError(payload, global);
      if (invalid !== null) {
        setError(invalid);
        return;
      }
      const backup = payload as unknown as GlobalState;
      const summary = describeSummary(summarizeMeet(backup));
      const question =
        `Se reemplaza el torneo de esta PC por la copia de «${copyName(copy)}» (${dateLabel(copy.updatedAt)}): ` +
        `${summary}. Antes se descarga un archivo con lo que hay ahora en esta PC.`;
      if (!window.confirm(question)) return;

      saveOpenlifterFile(global);
      load(stateFromWebBackup(backup, global));
      setCopies(null);
      setNotice(`Cargada la copia de «${copyName(copy)}»: ${summary}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: "8px", marginBottom: "8px" }}>
      <Button
        block
        className={styles.whiteButton}
        onClick={handleOpen}
        disabled={!linked || busy}
      >
        {busy && copies === null ? "Consultando la web…" : "Recuperar de la web"}
      </Button>
      {!linked ? (
        <small className="text-muted">Abrí el torneo desde la web para ver sus copias de respaldo.</small>
      ) : null}
      {error !== null && copies === null ? (
        <Alert variant="danger" className="mt-2 mb-0">
          {error}
        </Alert>
      ) : null}
      {notice !== null ? (
        <Alert variant="success" className="mt-2 mb-0" onClose={() => setNotice(null)} dismissible>
          {notice}
        </Alert>
      ) : null}

      <Modal show={copies !== null} onHide={() => setCopies(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Recuperar de la web</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {copies !== null && copies.length === 0 ? (
            <p className="mb-0">Todavía no hay copias de respaldo de este torneo.</p>
          ) : null}
          {copies?.map((copy) => (
            <div key={copy.deviceId} className="d-flex align-items-center justify-content-between border-bottom py-2">
              <div>
                <strong>{copyName(copy)}</strong>
                <div className="small text-muted">
                  {dateLabel(copy.updatedAt)} · {copy.entryCount} atletas
                </div>
              </div>
              <Button variant="primary" size="sm" disabled={busy} onClick={() => handleLoad(copy)}>
                Cargar
              </Button>
            </div>
          ))}
          {error !== null && copies !== null ? (
            <Alert variant="danger" className="mt-3 mb-0">
              {error}
            </Alert>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};

const mapState = (state: GlobalState): StateProps => ({ global: state });

const mapDispatch = (dispatch: Dispatch): DispatchProps => ({
  load: (state) => dispatch(overwriteStore(state)),
});

export const WebRestoreButton = connect(mapState, mapDispatch)(WebRestoreButtonInner);

export default WebRestoreButton;
